/**
 * Bailey's idle antics: the little things he gets up to while nobody needs
 * him (brushing his hair, sweeping up, sending emails, taking selfies...).
 *
 * Each antic is a rig state of the same name (see antics.js for the poses).
 * This file is only the timetable, so the page can schedule antics and time
 * its effects without loading Three.js.
 *
 *   duration  seconds, matching the pose in antics.js
 *   fx        page effects to play over the pet, at `at` seconds. x and y
 *             are fractions of the pet's box (0,0 top left); see anticFx.js
 */
export const IDLE_ANTICS = {
    brush: {
        label: 'Brushing his hair',
        duration: 7.4,
        fx: [{ at: 6.0, type: 'sparkle', x: 0.5, y: 0.12 }],
    },
    sweep: {
        label: 'Sweeping up',
        duration: 8.0,
        fx: [
            // At the end of each swish: right, then left.
            ...[0.68, 1.98, 3.28, 4.58].map((at) => ({ at, type: 'dust', x: 0.72, y: 0.95 })),
            ...[1.33, 2.63, 3.93, 5.23].map((at) => ({ at, type: 'dust', x: 0.44, y: 0.95 })),
            { at: 6.0, type: 'sparkle', x: 0.45, y: 0.9 },
        ],
    },
    emails: {
        label: 'Sending emails',
        duration: 8.6,
        fx: [2.2, 4.4, 6.4].map((at) => ({ at: at + 0.1, type: 'mail', x: 0.5, y: 0.62 })),
    },
    selfie: {
        label: 'Taking a selfie',
        duration: 7.6,
        fx: [
            ...[1.7, 3.3, 4.9].map((at) => ({ at, type: 'flash', x: 0.8, y: 0.2 })),
            { at: 6.0, type: 'hearts', x: 0.84, y: 0.34 },
        ],
    },
    nap: {
        label: 'Having a little nap',
        duration: 11.0,
        fx: [
            ...[2.9, 4.7, 6.5].map((at) => ({ at, type: 'zzz', x: 0.62, y: 0.2 })),
            { at: 8.45, type: 'alert', x: 0.5, y: 0.04 },
        ],
    },
    dance: {
        label: 'Having a little dance',
        duration: 6.5,
        fx: [0.4, 1.4, 2.4, 3.4, 4.4, 5.3].map((at, i) => ({ at, type: 'note', x: i % 2 ? 0.2 : 0.8, y: 0.22 })),
    },
    sneeze: {
        label: 'Sneezing',
        duration: 3.8,
        fx: [{ at: 1.72, type: 'sneeze', x: 0.5, y: 0.5 }],
    },
    bored: {
        label: 'Waiting patiently',
        duration: 6.0,
        fx: [{ at: 2.8, type: 'sigh', x: 0.55, y: 0.5 }],
    },
};

export const IDLE_ANTIC_IDS = Object.keys(IDLE_ANTICS);

export const isAntic = (state) => Object.prototype.hasOwnProperty.call(IDLE_ANTICS, state);
export const anticLabel = (state) => (isAntic(state) ? IDLE_ANTICS[state].label : '');

/** A random antic, avoiding the ones he has just done. */
export function pickAntic(recent = [], random = Math.random) {
    const fresh = IDLE_ANTIC_IDS.filter((id) => !recent.includes(id));
    const pool = fresh.length ? fresh : IDLE_ANTIC_IDS;
    return pool[Math.floor(random() * pool.length) % pool.length];
}
